import { t } from '../../../i18n';
import { sansAccents } from '../../../utils/langues';
import { chargerMaquette } from './donnees';

/**
 * Les lieux d'un campus, proposes quand on ecrit ou se tient un evenement.
 *
 * Ce sont les noms poses sur la maquette (`tools/campus/<serveur>.json`) :
 * les batiments, puis les salles, terrains et amphis de la visite. Chacun
 * garde sa position, si bien qu'en choisir un place l'evenement sur la
 * carte sans passer par le plan. Ils viennent des donnees de la maquette,
 * deja chargees par l'application, et ne coutent aucun appel.
 */
export interface LieuDuCampus {
  nom: string;
  /** Le batiment qui l'abrite, quand ce n'est pas lui-meme. */
  batiment: string | null;
  /** Ce qui s'ecrit dans le champ une fois choisi. */
  libelle: string;
  /** Sans position connue, le lieu se nomme sans se placer. */
  lat: number | null;
  lon: number | null;
  /** Tout ce par quoi on le cherche, sans accents. */
  cherche: string;
}

export async function lieuxDuCampus(serverId: string): Promise<LieuDuCampus[]> {
  const donnees = await chargerMaquette(serverId);
  if (!donnees?.zones) return [];

  const sansPlace = (donnees.autresLieux ?? []).map(
    (lieu): LieuDuCampus => ({
      nom: lieu.nom,
      batiment: null,
      libelle: lieu.nom,
      lat: null,
      lon: null,
      cherche: sansAccents(`${lieu.nom} ${(lieu.alias ?? []).join(' ')}`),
    }),
  );

  // Les memes metres que la maquette, dans l'autre sens : voir `scene.ts`.
  const { lat: lat0, lon: lon0 } = donnees.origine;
  const M_LAT = 110_574;
  const M_LON = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const position = ([x, y]: number[]) => ({ lat: lat0 + y / M_LAT, lon: lon0 + x / M_LON });

  const places = donnees.zones.flatMap((zone) => {
    const alias = (zone.alias ?? []).join(' ');
    const batiment: LieuDuCampus = {
      nom: zone.nom,
      batiment: null,
      libelle: zone.nom,
      ...position(zone.p),
      cherche: sansAccents(`${zone.nom} ${alias}`),
    };
    const salles = (zone.visites ?? [])
      .filter((visite) => visite.nom !== zone.nom)
      .map(
        (visite): LieuDuCampus => ({
          nom: visite.nom,
          batiment: zone.nom,
          libelle: `${visite.nom}, ${zone.nom}`,
          ...position(visite.p),
          cherche: sansAccents(`${visite.nom} ${zone.nom} ${alias}`),
        }),
      );
    return [batiment, ...salles];
  });

  /*
    Les salles des plans d'etage : « D202 », puis ou elle est, l'aile et
    l'etage dans la langue du membre. On les cherche par leur numero, avec
    ou sans espace (« CTI 109 », « cti109 »), et par « salle ».
  */
  const numerotees = (donnees.salles ?? []).map((salle): LieuDuCampus => {
    const ou = `${t.sorties.creation.campusAile(salle.aile)}, ${t.sorties.creation.campusEtage(salle.etage)}`;
    return {
      nom: salle.nom,
      batiment: ou,
      libelle: `${salle.nom}, ${ou}`,
      ...position(salle.p),
      cherche: sansAccents(`${salle.nom} ${salle.nom.replace(/\s+/g, '')} salle ${ou}`),
    };
  });
  const espaces = (donnees.espaces ?? []).map(
    (lieu): LieuDuCampus => ({
      nom: lieu.nom,
      batiment: lieu.etage > 0 ? t.sorties.creation.campusEtage(lieu.etage) : null,
      libelle: lieu.nom,
      ...position(lieu.p),
      cherche: sansAccents(lieu.nom),
    }),
  );
  return [...places, ...espaces, ...numerotees, ...sansPlace];
}

/**
 * Ajoute les lieux deja ecrits par d'autres, sans doublon.
 *
 * Un nom deja connu de la maquette garde sa fiche, plus complete ; un
 * lieu nouveau — « Salle 0.08 » — entre dans la liste tel qu'on l'a ecrit.
 */
export function avecLesLieuxDonnes(
  lieux: LieuDuCampus[],
  donnes: { nom: string; lat: number | null; lon: number | null }[],
): LieuDuCampus[] {
  const connus = new Set(lieux.flatMap((lieu) => [sansAccents(lieu.libelle), sansAccents(lieu.nom)]));
  const nouveaux = donnes
    .filter((lieu) => !connus.has(sansAccents(lieu.nom)))
    .map(
      (lieu): LieuDuCampus => ({
        nom: lieu.nom,
        batiment: null,
        libelle: lieu.nom,
        lat: lieu.lat,
        lon: lieu.lon,
        cherche: sansAccents(lieu.nom),
      }),
    );
  return [...lieux, ...nouveaux];
}

/**
 * Ceux qui repondent a ce qu'on tape : chaque mot doit s'y retrouver.
 *
 * Avec quatre cents salles, « d2 » en trouve des dizaines : celles dont le
 * nom commence par ce qu'on tape passent devant, puis celles qui le
 * contiennent, puis le reste, chaque groupe dans l'ordre des plans.
 */
export function chercherSurLeCampus(lieux: LieuDuCampus[], texte: string, combien = 8): LieuDuCampus[] {
  const mots = sansAccents(texte).split(/\s+/).filter(Boolean);
  if (mots.length === 0) return [];
  const colle = mots.join('');
  const rang = (lieu: LieuDuCampus) => {
    const nom = sansAccents(lieu.nom).replace(/\s+/g, '');
    return nom.startsWith(colle) ? 0 : nom.includes(colle) ? 1 : 2;
  };
  return lieux
    .filter((lieu) => mots.every((mot) => lieu.cherche.includes(mot)))
    .map((lieu, ordre) => ({ lieu, ordre, rang: rang(lieu) }))
    .sort(
      (a, b) =>
        a.rang - b.rang ||
        // Trouve par un autre nom (« gymnase ») : le batiment d'abord, puis ce qu'il abrite.
        (a.rang === 2 ? a.ordre - b.ordre : a.lieu.nom.localeCompare(b.lieu.nom, 'fr', { numeric: true })),
    )
    .slice(0, combien)
    .map(({ lieu }) => lieu);
}
